import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { SparkleIcon, HeartIcon, AlertIcon, RefreshIcon, ProfileIcon, CheckIcon } from './BespokeIcons';

/**
 * MindfulSOS - Emergency timeout and mindful intervention tools
 * 
 * Components for creating peaceful pauses and ensuring all voices are heard:
 * - TimeoutSOS: Emergency timeout with Tibetan chime
 * - StayPresentReminder: Popup to focus on the now
 * - RolePlaySuggestion: Popup suggesting perspective-taking
 * - InstantSummary: Poetic NVC summary of all inputs
 */

// Tibetan singing bowl sound generator using Web Audio API
export const useTibetanChime = () => {
  const audioContextRef = useRef(null);
  
  const playChime = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      const ctx = new AudioContext();
      const now = ctx.currentTime;
      
      // Tibetan bowl frequencies - fundamental + harmonics
      const frequencies = [
        { freq: 220, gain: 0.4, decay: 4 },     // Fundamental (A3)
        { freq: 440, gain: 0.25, decay: 3.5 },  // 2nd harmonic
        { freq: 660, gain: 0.15, decay: 3 },    // 3rd harmonic
        { freq: 880, gain: 0.1, decay: 2.5 },   // 4th harmonic
        { freq: 1100, gain: 0.08, decay: 2 },   // 5th harmonic
      ];
      
      // Master gain for overall volume
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.3, now);
      masterGain.connect(ctx.destination);
      
      frequencies.forEach(({ freq, gain, decay }) => {
        // Create oscillator
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now);
        
        // Create gain envelope
        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(gain, now + 0.02); // Quick attack
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + decay); // Slow decay
        
        // Add slight vibrato for warmth
        const vibrato = ctx.createOscillator();
        vibrato.frequency.setValueAtTime(5, now); // 5Hz vibrato
        const vibratoGain = ctx.createGain();
        vibratoGain.gain.setValueAtTime(2, now); // 2Hz deviation
        vibrato.connect(vibratoGain);
        vibratoGain.connect(osc.frequency);
        vibrato.start(now);
        vibrato.stop(now + decay);
        
        osc.connect(gainNode);
        gainNode.connect(masterGain);
        osc.start(now);
        osc.stop(now + decay);
      });
      
      // Clean up after sound finishes
      setTimeout(() => {
        ctx.close();
      }, 5000);
      
    } catch (e) {
      console.log('Audio not supported:', e);
    }
  }, []);
  
  return { playChime };
};

// ============================================
// TIMEOUT SOS COMPONENT
// ============================================

export const TimeoutSOS = ({ 
  isOpen, 
  onClose,
  participantNames = []
}) => {
  const { playChime } = useTibetanChime();
  const [phase, setPhase] = useState('chime'); // chime, message
  
  useEffect(() => {
    if (isOpen) {
      setPhase('chime');
      playChime();
      
      // Transition to message after chime settles
      const timer = setTimeout(() => setPhase('message'), 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, playChime]);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-amber-500/30 max-w-lg"
        data-testid="timeout-sos-dialog"
      >
        {/* Animated breathing background */}
        <div className="absolute inset-0 overflow-hidden rounded-lg">
          <div 
            className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-amber-500/10 to-amber-500/5"
            style={{ animation: 'breathe 4s ease-in-out infinite' }}
          />
        </div>
        
        <div className="relative z-10">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-20 h-20 rounded-full bg-amber-500/20 border-2 border-amber-500/40 flex items-center justify-center mb-4">
              <div 
                className="w-12 h-12 rounded-full bg-amber-500/30"
                style={{ animation: phase === 'chime' ? 'ripple 2s ease-out' : 'none' }}
              />
            </div>
            <DialogTitle className="text-2xl text-amber-300 font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
              Peaceful Pause
            </DialogTitle>
            <DialogDescription className="text-white/60">
              A moment to breathe and reconnect
            </DialogDescription>
          </DialogHeader>
          
          {phase === 'message' && (
            <div className="space-y-6 py-4 animate-fadeIn">
              {/* Reassurance message */}
              <div className="p-5 bg-white/5 rounded-xl border border-white/10">
                <p className="text-white/90 text-center leading-relaxed">
                  Everything shared so far is <span className="text-amber-400">safely held</span>.
                </p>
                <p className="text-white/70 text-center mt-3 text-sm leading-relaxed">
                  Each person's words, feelings, and needs are being honored. 
                  No voice will be lost. We can review everything together.
                </p>
              </div>
              
              {/* When overwhelmed guidance */}
              <div className="p-4 bg-purple-900/20 rounded-xl border border-purple-500/30">
                <p className="text-purple-200/90 text-xs font-medium mb-2">When overwhelmed with pain or anger:</p>
                <ol className="text-purple-200/70 text-xs space-y-1.5 list-decimal list-inside">
                  <li><span className="text-purple-300">Stop and breathe</span> — pause before reacting</li>
                  <li><span className="text-purple-300">Identify judgmental thoughts</span> — notice the story in your head</li>
                  <li><span className="text-purple-300">Connect with your needs</span> — what are you longing for?</li>
                  <li><span className="text-purple-300">Express feelings and unmet needs</span> — without blame</li>
                </ol>
              </div>
              
              {/* Participants acknowledged */}
              {participantNames.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2">
                  {participantNames.map((name, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full">
                      <CheckIcon size={12} color="#10B981" />
                      <span className="text-emerald-300 text-sm">{name}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Gentle guidance */}
              <div className="text-center space-y-2">
                <p className="text-amber-300/80 text-sm italic">
                  "Take a breath. You will be heard."
                </p>
                <p className="text-white/40 text-xs">
                  When ready, continue at your own pace.
                </p>
              </div>
              
              {/* Close button */}
              <div className="flex justify-center pt-2">
                <Button
                  onClick={onClose}
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
                  data-testid="timeout-continue-btn"
                >
                  <SparkleIcon size={16} color="#FCD34D" className="mr-2" />
                  Continue When Ready
                </Button>
              </div>
            </div>
          )}
        </div>
        
        <style>{`
          @keyframes breathe {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.05); }
          }
          @keyframes ripple {
            0% { transform: scale(0.8); opacity: 1; }
            100% { transform: scale(2); opacity: 0; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.6s ease-out forwards;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

// Compact SOS trigger button
export const SOSTriggerButton = ({ onClick, className = '' }) => (
  <button
    onClick={onClick}
    className={`
      group relative p-2 rounded-full 
      bg-gradient-to-br from-rose-600/80 to-rose-700/80 
      hover:from-rose-500 hover:to-rose-600
      border border-rose-400/30
      shadow-lg shadow-rose-500/20
      transition-all duration-300 hover:scale-110
      ${className}
    `}
    title="Peaceful Pause (SOS)"
    data-testid="sos-trigger-btn"
  >
    <AlertIcon size={16} color="#FFFFFF" />
    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-rose-300/80 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
      Pause
    </span>
  </button>
);

// ============================================
// STAY PRESENT REMINDER
// ============================================

export const StayPresentReminder = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-gradient-to-b from-blue-900/95 via-slate-900 to-slate-900 border-blue-500/30 max-w-md"
        data-testid="stay-present-dialog"
      >
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mb-3">
            <SparkleIcon size={28} color="#60A5FA" />
          </div>
          <DialogTitle className="text-xl text-blue-300 font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
            Stay in the Present
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-3">
          <p className="text-white/80 text-center leading-relaxed">
            Try to focus on <span className="text-blue-400 font-medium">what's alive now</span>, 
            rather than events from the past.
          </p>
          
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <p className="text-white/60 text-sm leading-relaxed text-center">
              When we speak about past events, we can lose touch with our 
              <span className="text-emerald-400"> present feelings</span> and 
              <span className="text-amber-400"> current needs</span>.
            </p>
          </div>
          
          <div className="flex items-center justify-center gap-2 p-3 bg-rose-500/10 rounded-lg border border-rose-500/20">
            <AlertIcon size={16} color="#FDA4AF" />
            <p className="text-rose-300 text-sm">
              If things feel heated, use the <strong>SOS button</strong> to pause.
            </p>
          </div>
          
          <div className="text-center pt-2">
            <p className="text-white/40 text-xs italic">
              "Connection happens in the present moment"
            </p>
          </div>
        </div>
        
        <div className="flex justify-center pt-2">
          <Button
            onClick={onClose}
            className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30"
          >
            I Understand
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// ROLE PLAY SUGGESTION
// ============================================

export const RolePlaySuggestion = ({ 
  isOpen, 
  onClose,
  participantNames = []
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-gradient-to-b from-purple-900/95 via-slate-900 to-slate-900 border-purple-500/30 max-w-md"
        data-testid="role-play-dialog"
      >
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center mb-3">
            <ProfileIcon size={28} color="#A78BFA" />
          </div>
          <DialogTitle className="text-xl text-purple-300 font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
            Walk in Their Shoes
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-3">
          <p className="text-white/80 text-center leading-relaxed">
            Consider having a mediator or participant 
            <span className="text-purple-400 font-medium"> play the role of another person</span>.
          </p>
          
          <div className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
            <p className="text-white/70 text-sm leading-relaxed">
              Try to genuinely get into their perspective and speak as if you <em>are</em> them.
            </p>
            <p className="text-white/60 text-sm">
              The person being represented always acts as the <span className="text-amber-400 font-medium">director</span> — 
              they can correct, guide, or stop the role-play at any time.
            </p>
          </div>
          
          {participantNames.length > 1 && (
            <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <p className="text-purple-300 text-sm text-center">
                Who would like to speak as someone else?
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-2">
                {participantNames.map((name, i) => (
                  <span key={i} className="px-2 py-1 bg-purple-500/20 rounded text-purple-200 text-xs">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div className="text-center pt-2">
            <p className="text-white/40 text-xs italic">
              "Understanding grows when we see through another's eyes"
            </p>
          </div>
        </div>
        
        <div className="flex justify-center pt-2">
          <Button
            onClick={onClose}
            className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30"
          >
            Got It
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// INSTANT SUMMARY (Poetic NVC Translation)
// ============================================

export const InstantSummary = ({ 
  isOpen, 
  onClose,
  data = {} // { participants: [{ name, observation, feelings, needs, empathyGuesses }], gold, title }
}) => {
  const { playChime } = useTibetanChime();
  const [showContent, setShowContent] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setShowContent(false);
      playChime();
      
      // Show content after chime
      const timer = setTimeout(() => setShowContent(true), 800);
      return () => clearTimeout(timer);
    }
  }, [isOpen, playChime]);
  
  // Generate poetic summary
  const generatePoeticSummary = () => {
    const participants = data.participants || [];
    const title = data.title || 'This Moment';
    const isAppreciation = data.isAppreciation || false;
    
    if (participants.length === 0) {
      return {
        title: "The Space Between",
        verses: ["No words have been shared yet.", "The canvas awaits your truth."]
      };
    }
    
    const verses = [];
    
    // Opening
    verses.push(`In the space called "${title}",`);
    verses.push(isAppreciation ? "gratitude flows..." : "voices seek to be understood...");
    verses.push("");
    
    // Each participant - their own expressions
    participants.forEach((p, i) => {
      if (p.observation || p.feelings || p.needs) {
        verses.push(`— ${p.name || 'A Voice'} —`);
        
        if (p.observation) {
          verses.push(`Witnessed: "${p.observation.slice(0, 100)}${p.observation.length > 100 ? '...' : ''}"`);
        }
        if (p.feelings) {
          verses.push(`Feeling: ${p.feelings.slice(0, 80)}${p.feelings.length > 80 ? '...' : ''}`);
        }
        if (p.needs) {
          verses.push(`Needing: ${p.needs.slice(0, 80)}${p.needs.length > 80 ? '...' : ''}`);
        }
        
        // Thank You expressions (appreciation mode)
        if (p.appreciationObservation || p.appreciationFeeling || p.appreciationNeedMet) {
          verses.push("");
          verses.push("— Thank You —");
          if (p.appreciationObservation) {
            verses.push(`What you did: "${p.appreciationObservation.slice(0, 80)}${p.appreciationObservation.length > 80 ? '...' : ''}"`);
          }
          if (p.appreciationFeeling) {
            verses.push(`What I feel: ${p.appreciationFeeling.slice(0, 80)}${p.appreciationFeeling.length > 80 ? '...' : ''}`);
          }
          if (p.appreciationNeedMet) {
            verses.push(`The need met: ${p.appreciationNeedMet.slice(0, 80)}${p.appreciationNeedMet.length > 80 ? '...' : ''}`);
          }
        }
        
        verses.push("");
      }
    });
    
    // Empathy bridges - what each person wondered about the other
    const hasEmpathyGuesses = participants.some(p => p.empathyGuesses && Object.keys(p.empathyGuesses).length > 0);
    if (hasEmpathyGuesses) {
      verses.push("— Bridges of Empathy —");
      verses.push("");
      
      participants.forEach(p => {
        if (p.empathyGuesses) {
          Object.entries(p.empathyGuesses).forEach(([targetId, guesses]) => {
            // Skip if person is wondering about themselves (Brad about Brad)
            if (targetId === p.id) {
              return;
            }
            
            const targetName = participants.find(t => t.id === targetId)?.name || 'the other';
            if (guesses.red || guesses.green) {
              verses.push(`${p.name || 'Someone'} wondered about ${targetName}:`);
              if (guesses.red) {
                verses.push(`  "Perhaps you're feeling ${guesses.red.slice(0, 60)}${guesses.red.length > 60 ? '...' : ''}?"`);
              }
              if (guesses.green) {
                verses.push(`  "Maybe you're needing ${guesses.green.slice(0, 60)}${guesses.green.length > 60 ? '...' : ''}?"`);
              }
              verses.push("");
            }
          });
        }
      });
    }
    
    // Closing / Gold
    if (data.gold) {
      verses.push(isAppreciation ? "— Celebration —" : "— The Golden Thread —");
      verses.push(`"${data.gold.slice(0, 150)}${data.gold.length > 150 ? '...' : ''}"`);
    } else {
      verses.push(isAppreciation ? "The celebration awaits..." : "The golden thread awaits discovery...");
    }
    
    return { title, verses };
  };
  
  const summary = generatePoeticSummary();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-gradient-to-b from-amber-900/95 via-slate-900 to-slate-900 border-amber-500/30 max-w-xl max-h-[80vh] overflow-y-auto"
        data-testid="instant-summary-dialog"
      >
        {/* Decorative background */}
        <div className="absolute inset-0 overflow-hidden rounded-lg opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/30 via-transparent to-transparent" />
        </div>
        
        <div className="relative z-10">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mb-3">
              <RefreshIcon size={28} color="#FBBF24" className={showContent ? '' : 'animate-spin'} />
            </div>
            <DialogTitle className="text-2xl text-amber-300 font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
              The Story So Far
            </DialogTitle>
            <DialogDescription className="text-white/50">
              A poetic reflection of what has been shared
            </DialogDescription>
          </DialogHeader>
          
          {showContent && (
            <div className="space-y-4 py-4 animate-fadeIn">
              {/* Poetic summary */}
              <div className="p-6 bg-white/5 rounded-xl border border-amber-500/20">
                <div className="space-y-2 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {summary.verses.map((verse, i) => (
                    <p 
                      key={i} 
                      className={`
                        ${verse === '' ? 'h-3' : ''}
                        ${verse.startsWith('—') ? 'text-amber-400 font-medium mt-4' : ''}
                        ${verse.startsWith('Witnessed:') ? 'text-blue-300/80 text-sm italic' : ''}
                        ${verse.startsWith('Feeling:') ? 'text-rose-300/80 text-sm' : ''}
                        ${verse.startsWith('Needing:') ? 'text-emerald-300/80 text-sm' : ''}
                        ${!verse.startsWith('—') && !verse.startsWith('Witnessed:') && !verse.startsWith('Feeling:') && !verse.startsWith('Needing:') && verse !== '' ? 'text-white/70' : ''}
                      `}
                    >
                      {verse}
                    </p>
                  ))}
                </div>
              </div>
              
              {/* NVC reminder */}
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-white/50 text-xs">
                  <span className="text-blue-400">Observations</span> · 
                  <span className="text-rose-400"> Feelings</span> · 
                  <span className="text-emerald-400"> Needs</span> · 
                  <span className="text-amber-400"> Requests</span>
                </p>
                <p className="text-white/40 text-xs mt-1 italic">
                  The language of connection
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-center pt-4">
            <Button
              onClick={onClose}
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
            >
              <HeartIcon size={16} color="#FCD34D" className="mr-2" />
              Close
            </Button>
          </div>
        </div>
        
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.8s ease-out forwards;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

// Summary trigger button
export const SummaryTriggerButton = ({ onClick, className = '' }) => (
  <button
    onClick={onClick}
    className={`
      group relative p-2 rounded-full 
      bg-gradient-to-br from-amber-600/80 to-amber-700/80 
      hover:from-amber-500 hover:to-amber-600
      border border-amber-400/30
      shadow-lg shadow-amber-500/20
      transition-all duration-300 hover:scale-110
      ${className}
    `}
    title="Instant Summary"
    data-testid="summary-trigger-btn"
  >
    <RefreshIcon size={16} color="#FFFFFF" />
    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-amber-300/80 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
      Summary
    </span>
  </button>
);

export default TimeoutSOS;
